var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { PublicKey } from "near-api-js/lib/utils";
import { KeyType } from "near-api-js/lib/utils/key_pair";
import { bip32PathToBytes } from "./utils";
// 128 - 5 service bytes
var CHUNK_SIZE = 123;
var CLA = 0x80;
var INS_GET_PUBLIC_KEY = 4;
var INS_GET_ADDRESS = 5;
var INS_SIGN = 2;
var P1_LAST_CHUNK = 0x80;
var NETWORK_ID = "W".charCodeAt(0);
/**
 * NEAR API
 *
 * @example
 * import Near from "@ledgerhq/hw-app-near";
 * const near = new Near(transport)
 */
var Near = /** @class */ (function () {
    function Near(transport) {
        this.transport = transport;
        transport.decorateAppAPIMethods(this, ["getPublicKey", "getAddress", "sign"], "NEAR");
    }
    /**
     * @param path
     * @option verify - if true, user must verify if the address is correct on the device
     * @return an object with a publicKey and address
     * @example
     * near.getAddress("44'/397'/0'/0'/0'", true).then(o => o.address)
     */
    Near.prototype.getAddress = function (path, verify) {
        return __awaiter(this, void 0, void 0, function () {
            var client, rawPublicKey, publicKey;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, createClient(this.transport)];
                    case 1:
                        client = _a.sent();
                        if (!verify) return [3 /*break*/, 3];
                        return [4 /*yield*/, client.getAddress(path)];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3: return [4 /*yield*/, client.getPublicKey(path, false)];
                    case 4:
                        rawPublicKey = _a.sent();
                        publicKey = new PublicKey({
                            keyType: KeyType.ED25519,
                            data: rawPublicKey
                        });
                        return [2 /*return*/, {
                                address: rawPublicKey.toString("hex"),
                                publicKey: publicKey.toString()
                            }];
                }
            });
        });
    };
    /**
     * @param transaction
     * @param path
     * @return a signature to be broadcasted to the chain
     */
    Near.prototype.signTransaction = function (transaction, path) {
        return __awaiter(this, void 0, void 0, function () {
            var client, signature;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, createClient(this.transport)];
                    case 1:
                        client = _a.sent();
                        return [4 /*yield*/, client.sign(transaction, path)];
                    case 2:
                        signature = _a.sent();
                        return [2 /*return*/, signature];
                }
            });
        });
    };
    return Near;
}());
export default Near;
function createClient(transport) {
    return __awaiter(this, void 0, void 0, function () {
        return __generator(this, function (_a) {
            return [2 /*return*/, {
                    transport: transport,
                    getPublicKey: function (path, verify) {
                        return __awaiter(this, void 0, void 0, function () {
                            var response;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, this.transport.send(CLA, INS_GET_PUBLIC_KEY, verify ? 0 : 1, NETWORK_ID, bip32PathToBytes(path))];
                                    case 1:
                                        response = _a.sent();
                                        return [2 /*return*/, Buffer.from(response.subarray(0, -2))];
                                }
                            });
                        });
                    },
                    getAddress: function (path) {
                        return __awaiter(this, void 0, void 0, function () {
                            var response;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, this.transport.send(CLA, INS_GET_ADDRESS, 0, NETWORK_ID, bip32PathToBytes(path))];
                                    case 1:
                                        response = _a.sent();
                                        return [2 /*return*/, Buffer.from(response.subarray(0, -2))];
                                }
                            });
                        });
                    },
                    sign: function (transactionData, path) {
                        return __awaiter(this, void 0, void 0, function () {
                            var allData, offset, chunk, isLastChunk, response;
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0:
                                        transactionData = Buffer.from(transactionData);
                                        allData = Buffer.concat([bip32PathToBytes(path), transactionData]);
                                        offset = 0;
                                        _a.label = 1;
                                    case 1:
                                        if (!(offset < allData.length)) return [3 /*break*/, 4];
                                        chunk = Buffer.from(allData.subarray(offset, offset + CHUNK_SIZE));
                                        isLastChunk = offset + CHUNK_SIZE >= allData.length;
                                        return [4 /*yield*/, this.transport.send(CLA, INS_SIGN, isLastChunk ? P1_LAST_CHUNK : 0, NETWORK_ID, chunk)];
                                    case 2:
                                        response = _a.sent();
                                        if (isLastChunk) {
                                            return [2 /*return*/, Buffer.from(response.subarray(0, -2))];
                                        }
                                        _a.label = 3;
                                    case 3:
                                        offset += CHUNK_SIZE;
                                        return [3 /*break*/, 1];
                                    case 4: return [2 /*return*/];
                                }
                            });
                        });
                    }
                }];
        });
    });
}
//# sourceMappingURL=Near.js.map